import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, sgd, resolveImage, preorderState } from "@/lib/api";
import { useCart, realPieces } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { Heart, ArrowLeft, ChevronDown, Ruler } from "lucide-react";
import { toast } from "sonner";
import SizeGuideDrawer from "@/components/SizeGuideDrawer";

// Label a piece block based on its type: top/bottom/dress/outer get specific labels,
// anything else (e.g. a single-piece "set") is just "Size"
const pieceLabel = (p, isOnly) => {
  if (isOnly) return `Size`;
  const t = (p.type || "").toLowerCase();
  if (t === "top") return "Top";
  if (t === "bottom") return "Bottom";
  if (t === "dress") return "Dress";
  if (t === "outer") return "Outerwear";
  return "Size";
};

export default function LookDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [look, setLook] = useState(null);
  const [settings, setSettings] = useState({});
  const [choices, setChoices] = useState({});
  const [expand, setExpand] = useState(null);
  const [sgOpen, setSgOpen] = useState(false);
  const { addComplete } = useCart();
  const { has, toggle } = useWishlist();

  useEffect(() => {
    api.get(`/looks/${slug}`).then(({ data }) => {
      setLook(data);
      // If this URL was an old slug (now in slug_history), replace the URL with
      // the canonical one so bookmarks and shares always land on the fresh path.
      if (data?.slug && data.slug !== slug) {
        navigate(`/looks/${data.slug}`, { replace: true });
      }
    });
  }, [slug, navigate]);
  useEffect(() => { api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {}); }, []);

  const pieces = useMemo(() => realPieces(look), [look]);
  const isOnlyOne = pieces.length === 1;

  // Default-select the first size + first color of each piece once the look loads
  useEffect(() => {
    if (!pieces.length) return;
    setChoices(prev => {
      const next = { ...prev };
      pieces.forEach(p => {
        if (!next[p.id]) {
          next[p.id] = {
            size: (p.sizes || [])[0] || "",
            color: (p.colors || [])[0] || "",
          };
        }
      });
      return next;
    });
  }, [pieces]);

  if (!look) return <div className="min-h-[60vh] flex items-center justify-center text-[13px] text-[#4A464A]/60">Loading…</div>;

  const addToBag = () => {
    const selections = pieces.map(p => ({
      piece_id: p.id,
      piece_type: p.type,
      piece_name: p.name,
      sizes: p.sizes || [],
      colors: p.colors || [],
      size: choices[p.id]?.size || (p.sizes || [])[0] || "",
      color: choices[p.id]?.color || (p.colors || [])[0] || "",
    }));
    addComplete(look, selections);
    toast.success(`LOOK ${look.number} · ${look.name} added ♡`);
  };

  const setChoice = (pid, patch) => setChoices(prev => ({ ...prev, [pid]: { ...(prev[pid] || {}), ...patch } }));

  const po = preorderState(settings);
  const anySoldOut = pieces.some(p => Number(p.stock || 0) === 0);

  return (
    <div data-testid={`look-detail-${slug}`} className="pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-6">
        <Link to="/looks" className="text-[11px] uppercase tracking-editorial inline-flex items-center gap-2"><ArrowLeft size={13} strokeWidth={1.2} /> All Looks</Link>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-8 grid md:grid-cols-2 gap-8 md:gap-16">
        {/* Gallery */}
        <div className="space-y-3">
          <img src={resolveImage(look.hero_image)} alt={look.name} className="w-full aspect-[3/4] object-cover" />
          <div className="grid grid-cols-2 gap-3">
            {(look.gallery || []).slice(0, 4).map((g, i) => (
              <img key={i} src={resolveImage(g)} alt={`${look.name} ${i}`} className="w-full aspect-[3/4] object-cover" />
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="md:sticky md:top-32 self-start">
          <div className="text-[11px] tracking-editorial uppercase text-[#C3B1C5]">LOOK {look.number}</div>
          <h1 className="font-serif-display text-[42px] md:text-[56px] leading-[1.05] mt-2 tracking-tight">{look.name}</h1>
          <p className="mt-4 text-[14px] italic text-[#4A464A]/70">"{look.description}"</p>

          <div className="mt-6 flex items-baseline gap-3">
            <div className="font-serif-display text-[26px]">{sgd(look.bundle_price)}</div>
            <div className="text-[11px] uppercase tracking-editorial text-[#4A464A]/50">Complete Look</div>
          </div>

          {/* Per-piece size + colour */}
          <div className="mt-8 space-y-7">
            {pieces.map(p => {
              const label = pieceLabel(p, isOnlyOne);
              const sel = choices[p.id] || {};
              const stock = Number(p.stock || 0);
              const threshold = Math.max(0, parseInt(settings.low_stock_threshold || "3", 10) || 0);
              const soldOut = stock === 0;
              const lowStock = !soldOut && threshold > 0 && stock <= threshold;
              return (
                <div key={p.id} data-testid={`piece-block-${p.type}`}>
                  <div className="flex items-center justify-between text-[12px] uppercase tracking-editorial mb-2">
                    <span className="flex items-center gap-2">
                      {label}{isOnlyOne ? "" : ` · ${p.name}`}
                      {soldOut && <span className="normal-case tracking-normal text-[10px] px-2 py-0.5" style={{ background: "#EBE5ED", color: "#4A464A" }} data-testid={`soldout-chip-${p.type}`}>Sold out</span>}
                    </span>
                    {(p.sizes || []).length > 0 && (
                      <button type="button" onClick={() => setSgOpen(true)} data-testid={`size-guide-link-${p.type}`} className="normal-case tracking-normal text-[11px] inline-flex items-center gap-1 border-b pb-[1px] text-[#4A464A]/70 hover:text-[#4A464A]" style={{ borderColor: "#C3B1C5" }}>
                        <Ruler size={11} strokeWidth={1.3} /> Size Guide
                      </button>
                    )}
                  </div>
                  {(p.sizes || []).length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {p.sizes.map(sz => (
                        <button key={sz} data-testid={`${p.type}-size-${sz}`} onClick={() => !soldOut && setChoice(p.id, { size: sz })} disabled={soldOut}
                                className="min-w-11 h-11 px-3 text-[13px] border transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:line-through"
                                style={{ background: sel.size === sz && !soldOut ? "#4A464A" : "transparent", color: sel.size === sz && !soldOut ? "#FDFBF7" : "#4A464A", borderColor: sel.size === sz && !soldOut ? "#4A464A" : "#EBE5ED" }}>{sz}</button>
                      ))}
                    </div>
                  )}
                  {lowStock && (
                    <div className="mt-2 text-[11px] italic" style={{ color: "#C3B1C5" }} data-testid={`low-stock-${p.type}`}>
                      Only {stock} left ♡
                    </div>
                  )}
                  {(p.colors || []).length > 0 && (
                    <div className="mt-3">
                      <div className="text-[11px] uppercase tracking-editorial text-[#4A464A]/60 mb-2">Colour · <span className="text-[#4A464A]">{sel.color || "—"}</span></div>
                      <div className="flex gap-2 flex-wrap">
                        {p.colors.map(c => (
                          <button key={c} data-testid={`${p.type}-color-${c}`} onClick={() => !soldOut && setChoice(p.id, { color: c })} disabled={soldOut}
                                  className="px-3 h-9 text-[12px] border transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                                  style={{ background: sel.color === c && !soldOut ? "#EBE5ED" : "transparent", color: "#4A464A", borderColor: sel.color === c && !soldOut ? "#4A464A" : "#EBE5ED" }}>{c}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex gap-3">
            {po.active && (
              <div className="w-full" data-testid="preorder-note-look">
                <div className="text-[12px] px-3 py-2.5 mb-3" style={{ background: "#EBE5ED", color: "#4A464A" }}>
                  Pre-order ♡ Orders close {po.closeDateFmt}. All orders ship from {po.shipDateFmt}.
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <button data-testid="add-to-bag" onClick={addToBag} disabled={po.closed || anySoldOut} className="flex-1 py-4 text-[12px] uppercase tracking-editorial disabled:cursor-not-allowed disabled:opacity-60" style={{ background: "#4A464A", color: "#FDFBF7" }}>
              {anySoldOut ? "Sold out · back soon" : (po.closed ? "Pre-orders closed · back soon" : (po.active ? "Pre-order Complete Look" : "Add Complete Look to Bag"))}
            </button>
            <button onClick={() => toggle(look.id)} data-testid="wishlist-btn" className="w-14 border flex items-center justify-center" style={{ borderColor: "#4A464A" }}>
              <Heart size={16} strokeWidth={1.2} fill={has(look.id) ? "#C3B1C5" : "none"} stroke={has(look.id) ? "#C3B1C5" : "#4A464A"} />
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-editorial text-[#4A464A]/60">
            <span>◐</span>
            <span>Pay by <span style={{ color: "#4A464A" }}>card</span> or <span style={{ color: "#4A464A" }}>PayNow</span> · SGD</span>
          </div>

          {/* Accordions */}
          <div className="mt-10 border-t" style={{ borderColor: "#EBE5ED" }}>
            {[
              { key: "story", label: "The Story", body: look.story || look.description },
              { key: "details", label: "Materials & Fit", body: (look.materials_fit || "").trim() },
              { key: "measure", label: "Measurements", body: (look.garment_measurements || "").trim() },
              { key: "ship", label: "Shipping & Returns", body: `${po.active ? `Soft launch: all items are pre-order. Orders close ${po.closeDateFmt} and ship from ${po.shipDateFmt}. ` : ""}We currently ship within Singapore only. Free delivery on orders over S$88. Exchanges for another size or another look are accepted within 7 days of delivery. Items must be unworn, unwashed and with tags on.` },
            ].filter(a => (a.body || "").trim()).map(a => (
              <div key={a.key} className="border-b" style={{ borderColor: "#EBE5ED" }}>
                <button onClick={() => setExpand(expand === a.key ? null : a.key)} className="w-full flex items-center justify-between py-4 text-[12px] uppercase tracking-editorial" data-testid={`accordion-${a.key}`}>
                  {a.label} <ChevronDown size={14} strokeWidth={1.2} className={`transition-transform ${expand === a.key ? "rotate-180" : ""}`} />
                </button>
                {expand === a.key && <div className="pb-5 text-[13px] leading-relaxed whitespace-pre-wrap text-[#4A464A]/75">{a.body}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Size Guide drawer */}
      <SizeGuideDrawer open={sgOpen} onClose={() => setSgOpen(false)} garmentMeasurements={look.garment_measurements} lookName={`LOOK ${look.number} · ${look.name}`} />

      {/* mobile sticky bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 border-t px-5 py-3 flex items-center gap-3" style={{ background: "rgba(253,251,247,0.95)", borderColor: "#EBE5ED", backdropFilter: "blur(12px)" }}>
        <div>
          <div className="text-[10px] uppercase tracking-editorial text-[#C3B1C5]">Complete Look</div>
          <div className="font-serif-display text-[16px]">{sgd(look.bundle_price)}</div>
        </div>
        <button onClick={addToBag} data-testid="mobile-add-to-bag" disabled={po.closed || anySoldOut} className="flex-1 py-3 text-[11px] uppercase tracking-editorial disabled:opacity-60 disabled:cursor-not-allowed" style={{ background: "#4A464A", color: "#FDFBF7" }}>
          {anySoldOut ? "Sold out" : (po.closed ? "Pre-orders closed" : (po.active ? "Pre-order Now" : "Add to Bag"))}
        </button>
      </div>
    </div>
  );
}
